import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
  useGetInboxQuery,
  useArchiveConversationMutation
} from 'store/services/message/messageApi';
import { RootState } from 'store';
import { AssetUrls } from 'config/assets';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import './InboxPage.css';

export default function InboxPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Redux state for rate limiting, maintenance mode, and socket connection
  const isRateLimited = useSelector((state: RootState) => state.rateLimit.isRateLimited);
  const isMaintenanceMode = useSelector((state: RootState) => state.maintenance.isMaintenanceModeActive);
  const isSocketConnected = useSelector((state: RootState) => state.message.isConnected);

  // Socket-based polling: Use WebSocket when connected, fallback to 60s polling when disconnected
  // When socket is connected, inbox updates in real-time via message:new events
  const pollingInterval = (isRateLimited || isMaintenanceMode)
    ? 0
    : isSocketConnected ? 0 : 60000; // No polling when connected, 60s fallback when disconnected

  const { data, isLoading, error } = useGetInboxQuery(
    { page, pageSize: 25 },
    {
      pollingInterval,
      refetchOnMountOrArgChange: true,
    }
  );
  const [archiveConversation] = useArchiveConversationMutation();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleArchive = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await archiveConversation(conversationId).unwrap();
      toast.success('Conversation archived');
    } catch (error) {
      toast.error('Failed to archive conversation');
    }
  };

  const formatReceivedTime = (timestamp: string): string => {
    const messageDate = new Date(timestamp);
    const now = new Date();
    const diffHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

    if (diffHours < 24) {
      return messageDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return messageDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    }
  };

  // Filter conversations
  const filteredConversations = data?.conversations
    ?.filter(conv => {
      // Search filter
      if (debouncedSearch) {
        return conv.otherUser.username
          .toLowerCase()
          .includes(debouncedSearch.toLowerCase());
      }
      return true;
    })
    .filter(conv => {
      // Read/Unread filter
      if (filter === 'unread') return conv.unreadCount > 0;
      if (filter === 'read') return conv.unreadCount === 0;
      return true;
    });

  if (isLoading) {
    return (
      <div className="inbox-page">
        <div className="inbox-header">
          <h1>Inbox</h1>
          <p className="inbox-subheader">
            Read and respond to messages sent to you by other traders in the community.
          </p>
        </div>
        <div className="inbox-loading">Loading messages...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-page">
        <div className="inbox-header">
          <h1>Inbox</h1>
        </div>
        <div className="inbox-error">Failed to load messages. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="inbox-page">
      <div className="inbox-header">
        <h1>Inbox</h1>
        <p className="inbox-subheader">
          Read and respond to messages sent to you by other traders in the community.
        </p>
      </div>

      <div className="inbox-filters">
        <div className="inbox-search-container">
          <input
            type="text"
            placeholder="Search username or card name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="inbox-search"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="clear-search-button"
            >
              ×
            </button>
          )}
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as 'all' | 'unread' | 'read')}
          className="inbox-filter-select"
        >
          <option value="all">All</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
        </select>
      </div>

      {!filteredConversations || filteredConversations.length === 0 ? (
        <div className="inbox-empty">
          {debouncedSearch ? (
            <>
              <p>No messages match your search</p>
              <button onClick={() => setSearchQuery('')} className="clear-search-button-large">
                Clear search
              </button>
            </>
          ) : (
            <>
              <img
                src={`${AssetUrls.base}/assets/illustrations/empty-inbox.svg`}
                alt="No messages"
                className="empty-inbox-illustration"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <p>No messages yet</p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="inbox-table">
            <div className="inbox-table-header">
              <span className="col-username">Username</span>
              <span className="col-message">Message</span>
              <span className="col-received">Received</span>
              <span className="col-archive"></span>
            </div>

            {filteredConversations.map((conv) => (
              <div
                key={conv.conversationId}
                className={`inbox-row ${conv.unreadCount > 0 ? 'unread' : ''}`}
                onClick={() => navigate(`/inbox/${conv.conversationId}`)}
              >
                <div className="col-username">
                  <AvatarWithFlag
                    avatarUrl={conv.otherUser.avatarUrl}
                    username={conv.otherUser.username}
                    countryCode={conv.otherUser.country}
                    size={32}
                  />
                  <Link
                    to={`/profile/u/${encodeURIComponent(conv.otherUser.username)}`}
                    className="inbox-username inbox-username--link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {conv.otherUser.username}
                  </Link>
                  {conv.unreadCount > 0 && (
                    <span className="inbox-unread-count">{conv.unreadCount}</span>
                  )}
                </div>
                <div className="col-message">{conv.lastMessage}</div>
                <div className="col-received">{formatReceivedTime(conv.lastMessageAt)}</div>
                <div className="col-archive">
                  <button
                    className="archive-button"
                    onClick={(e) => handleArchive(conv.conversationId, e)}
                    title="Archive conversation"
                  >
                    <img
                      src={AssetUrls.icons.dropdowns.close}
                      alt="Archive"
                      style={{ width: 16, height: 16 }}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {data?.pagination && (
            <div className="inbox-pagination">
              <button
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="pagination-button"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {Math.ceil(data.pagination.total / data.pagination.pageSize)}
              </span>
              <button
                disabled={!data.pagination.hasMore}
                onClick={() => setPage(p => p + 1)}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
